import { useServicePrimaryTagListQuery } from "entities/tagTypes/queries/useServicePrimaryTagListQuery";
import { useTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { Tag } from "entities/tags/types";
import groupBy from "lodash/groupBy";
import { useCallback, useMemo, useState } from "react";
import { ComboGroupedElement } from "shared/components/Combobox/type";
import { getFilteredTagsBySearchValue } from "../utils";

export type ServiceTagsFilterApi = {
  selectedServiceTags: string[];
  handleSelectGroupedOptions: (values: string[]) => void;
  handleClearSelectedOptions: () => void;
  groupedServiceTags: ComboGroupedElement[];
  serviceTagsFilterGames: string;
  serviceTagsSearchQuery: string;
  handleSearchServiceTags: (query: string) => void;
};

export const useServiceTagsFilter = (): ServiceTagsFilterApi => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedServiceTagValues, setSelectedServiceTagValues] = useState<
    string[]
  >([]);
  const [searchedTags, setSearchedTags] = useState<ComboGroupedElement[]>([]);

  const { data: servicePrimaryTags = [] } = useServicePrimaryTagListQuery();
  const { data: serviceTags = [] } = useTagsListQuery();

  const serviceTagsByPrimaryTag = useMemo(() => {
    const tags: Record<string, Tag[]> = groupBy(serviceTags, "tagType");

    return tags;
  }, [serviceTags]);

  const groupedServiceTags: ComboGroupedElement[] = useMemo(() => {
    const primaryTagsMap = new Map(
      servicePrimaryTags?.map((tag) => [tag.id, tag.name]),
    );

    return Object.entries(serviceTagsByPrimaryTag)?.map(([id, tags]) => ({
      value: id,
      name: primaryTagsMap.get(id) || id,
      options: tags?.map((tag) => ({
        value: tag.id,
        name: tag.name,
      })),
    }));
  }, [servicePrimaryTags, serviceTagsByPrimaryTag]);

  const serviceTagsFilterGames = useMemo(() => {
    const tagsFilter =
      selectedServiceTagValues.length > 0
        ? `(${selectedServiceTagValues?.map((id) => `tags:${id}`).join(" OR ")})`
        : "";

    return [tagsFilter]?.filter(Boolean)?.join(" OR ");
  }, [selectedServiceTagValues]);

  const handleSelectGroupedOptions = (values: string[]) => {
    setSelectedServiceTagValues(values);
  };

  const handleSearchServiceTags = useCallback(
    (value: string) => {
      setSearchValue(value);

      const filteredTagsBySearchValue: ComboGroupedElement[] =
        getFilteredTagsBySearchValue(groupedServiceTags, value);

      setSearchedTags(filteredTagsBySearchValue);
    },
    [groupedServiceTags],
  );
  const serviceTagsWithSearch = useMemo(
    () => (searchValue ? searchedTags : groupedServiceTags),
    [searchValue, searchedTags, groupedServiceTags],
  );

  const handleClearSelectedOptions = () => {
    setSelectedServiceTagValues([]);
    setSearchValue("");
    setSearchedTags([]);
  };

  return {
    selectedServiceTags: selectedServiceTagValues,
    handleSelectGroupedOptions,
    handleClearSelectedOptions,
    groupedServiceTags: serviceTagsWithSearch,
    serviceTagsFilterGames,
    serviceTagsSearchQuery: searchValue,
    handleSearchServiceTags,
  };
};
